import React, { createContext, useContext, useState, ReactNode } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Tile, Group, DashboardConfig } from '../types';

interface DragDropContextType {
  draggedItem: DraggedItem | null;
  dragType: 'tile' | 'group' | null;
  isDragging: boolean;
  setDraggedItem: (item: DraggedItem | null) => void;
  setDragType: (type: 'tile' | 'group' | null) => void;
  handleTileDrop: (tileId: string, targetGroupId: string, newIndex?: number) => void;
  handleGroupReorder: (sourceGroupId: string, targetGroupId: string) => void;
}

interface DraggedItem {
  id: string;
  type: 'tile' | 'group';
  sourceGroupId?: string;
  data: Tile | Group;
}

interface DashboardContextLike {
  config: DashboardConfig | null;
  updateConfig: (config: DashboardConfig) => Promise<void>;
}

interface DragDropProviderProps {
  children: ReactNode;
  dashboardContext?: DashboardContextLike; // Optional dashboard context for preview mode
}

const DragDropContext = createContext<DragDropContextType | undefined>(undefined);

// Internal provider that takes explicit dashboard context
const DragDropProviderInternal: React.FC<DragDropProviderProps> = ({ children, dashboardContext }) => {
  const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
  const [dragType, setDragType] = useState<'tile' | 'group' | null>(null);

  const isDragging = !!draggedItem;

  // Get config and updateConfig from either passed context or default to null
  const config: DashboardConfig | null = dashboardContext?.config || null;
  const updateConfig: (config: DashboardConfig) => Promise<void> = dashboardContext?.updateConfig || (async () => {});

  const handleTileDrop = async (tileId: string, targetGroupId: string, newIndex?: number) => {
    if (!config || !draggedItem || draggedItem.type !== 'tile') {
      return;
    }

    const sourceGroupId = draggedItem.sourceGroupId;
    if (!sourceGroupId) {
      return;
    }

    if (sourceGroupId === targetGroupId && newIndex === undefined) {
      return;
    }

    // Create a deep copy of the configuration with tabs structure
    const newConfig = JSON.parse(JSON.stringify(config));
    let sourceGroup: Group | null = null;
    let targetGroup: Group | null = null;

    // Find source and target groups in tabs structure
    if (newConfig.tabs) {
      for (const tab of newConfig.tabs) {
        for (const group of tab.groups || []) {
          if (group.id === sourceGroupId) {
            sourceGroup = group;
          }
          if (group.id === targetGroupId) {
            targetGroup = group;
          }
        }
      }
    } else {
      // Fallback to legacy groups structure
      sourceGroup = newConfig.groups?.find((g: Group) => g.id === sourceGroupId);
      targetGroup = newConfig.groups?.find((g: Group) => g.id === targetGroupId);
    }

    if (!sourceGroup || !targetGroup) {
      return;
    }

    // Find and remove the tile from source group
    const tileIndex = sourceGroup.tiles.findIndex((t: Tile) => t.id === tileId);
    if (tileIndex === -1) {
      return;
    }

    const [tile] = sourceGroup.tiles.splice(tileIndex, 1);

    // Add tile to target group
    if (newIndex !== undefined && newIndex >= 0 && newIndex <= targetGroup.tiles.length) {
      targetGroup.tiles.splice(newIndex, 0, tile);
    } else {
      targetGroup.tiles.push(tile);
    }

    // Reorder tiles in both groups
    sourceGroup.tiles.forEach((t: Tile, index: number) => {
      t.order = index;
    });

    targetGroup.tiles.forEach((t: Tile, index: number) => {
      t.order = index;
    });

    // Update configuration
    try {
      await updateConfig({
        ...newConfig,
        metadata: {
          ...newConfig.metadata,
          lastModified: new Date().toISOString()
        }
      });
    } catch (error) {
      console.error('updateConfig failed:', error);
    }

    // Clear drag state
    setDraggedItem(null);
    setDragType(null);
  };

  const handleGroupReorder = async (sourceGroupId: string, targetGroupId: string) => {
    if (!config) {
      return;
    }

    // Create a deep copy of the configuration
    const newConfig = JSON.parse(JSON.stringify(config));
    let activeTab = null;

    // Find the active tab and handle group reordering within that tab
    if (newConfig.tabs) {
      activeTab = newConfig.tabs.find((tab: any) => tab.id === newConfig.activeTabId) || newConfig.tabs[0];
      
      if (!activeTab) {
        return;
      }

      const sourceIndex = activeTab.groups.findIndex((g: Group) => g.id === sourceGroupId);
      const targetIndex = activeTab.groups.findIndex((g: Group) => g.id === targetGroupId);

      if (sourceIndex === -1 || targetIndex === -1) {
        return;
      }

      // Remove source group and insert at target position
      const [sourceGroup] = activeTab.groups.splice(sourceIndex, 1);
      activeTab.groups.splice(targetIndex, 0, sourceGroup);

      // Update order values
      activeTab.groups.forEach((group: Group, index: number) => {
        group.order = index;
      });
    } else {
      // Fallback to legacy groups structure
      const newGroups = [...newConfig.groups];
      const sourceIndex = newGroups.findIndex((g: Group) => g.id === sourceGroupId);
      const targetIndex = newGroups.findIndex((g: Group) => g.id === targetGroupId);

      if (sourceIndex === -1 || targetIndex === -1) {
        return;
      }

      // Remove source group and insert at target position
      const [sourceGroup] = newGroups.splice(sourceIndex, 1);
      newGroups.splice(targetIndex, 0, sourceGroup);

      // Update order values
      newGroups.forEach((group: Group, index: number) => {
        group.order = index;
      });

      newConfig.groups = newGroups;
    }

    // Update configuration
    await updateConfig({
      ...newConfig,
      metadata: {
        ...newConfig.metadata,
        lastModified: new Date().toISOString()
      }
    });

    // Clear drag state
    setDraggedItem(null);
    setDragType(null);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <DragDropContext.Provider
        value={{
          draggedItem,
          dragType,
          isDragging,
          setDraggedItem,
          setDragType,
          handleTileDrop,
          handleGroupReorder,
        }}
      >
        {children}
      </DragDropContext.Provider>
    </DndProvider>
  );
};

// Main provider - requires dashboard context to be passed
export const DragDropProvider: React.FC<DragDropProviderProps> = ({ children, dashboardContext }) => {
  return (
    <DragDropProviderInternal dashboardContext={dashboardContext}>
      {children}
    </DragDropProviderInternal>
  );
};

export const useDragDrop = (): DragDropContextType => {
  const context = useContext(DragDropContext);
  if (!context) {
    throw new Error('useDragDrop must be used within a DragDropProvider');
  }
  return context;
}; 